import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { openInGoogleMaps, formatCoordinates } from '../utils/locationTracker';
import './SupervisorClockMonitor.css';

function SupervisorClockMonitor({ user, onBack }) {
  const [activeTab, setActiveTab] = useState('live');
  const [liveStatus, setLiveStatus] = useState([]);       // Who's clocked in RIGHT NOW
  const [allEvents, setAllEvents] = useState([]);          // All clock events for selected date
  const [technicians, setTechnicians] = useState([]);
  const [selectedDate, setSelectedDate] = useState(getLocalDateString());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Helper: get today's date as YYYY-MM-DD in local time
  function getLocalDateString(date = new Date()) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // Load technicians once
  useEffect(() => {
    loadTechnicians();
  }, []);

  // Live listener: subscribes to today's clock events in real-time
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const clockRef = collection(db, 'clockEvents');
    const q = query(
      clockRef,
      where('timestamp', '>=', today.getTime()),
      where('timestamp', '<', tomorrow.getTime()),
      orderBy('timestamp', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      computeLiveStatus(events);
      setLastUpdate(new Date());
      setLoading(false);
    }, (error) => {
      console.error('❌ Live clock listener error:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load historical events when selectedDate changes
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistoricalEvents(selectedDate);
    }
  }, [selectedDate, activeTab]);

  const loadTechnicians = async () => {
    try {
      const techRef = collection(db, 'users');
      const q = query(techRef, where('role', '==', 'technician'), where('active', '==', true));
      const snapshot = await getDocs(q);
      setTechnicians(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('❌ Error loading technicians:', error);
    }
  };

  // Build a per-technician "currently clocked in?" map from today's events
  const computeLiveStatus = (events) => {
    const statusMap = {};

    // Group by userId, keep only the most recent event per user
    events.forEach(event => {
      if (!statusMap[event.userId] || event.timestamp > statusMap[event.userId].timestamp) {
        statusMap[event.userId] = event;
      }
    });

    // Build list: only those whose last event is 'in'
    const live = Object.values(statusMap)
      .filter(event => event.type === 'in')
      .sort((a, b) => a.userName?.localeCompare(b.userName));

    setLiveStatus(live);
  };

  const loadHistoricalEvents = async (dateString) => {
    setLoading(true);
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      const start = new Date(year, month - 1, day, 0, 0, 0, 0);
      const end = new Date(year, month - 1, day, 23, 59, 59, 999);

      const clockRef = collection(db, 'clockEvents');
      const q = query(
        clockRef,
        where('timestamp', '>=', start.getTime()),
        where('timestamp', '<=', end.getTime()),
        orderBy('timestamp', 'asc')
      );

      const snapshot = await getDocs(q);
      const events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllEvents(events);
    } catch (error) {
      console.error('❌ Error loading historical events:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group history events by technician and compute hours worked
  const getAttendanceSummary = () => {
    const summary = {};

    allEvents.forEach(event => {
      if (!summary[event.userId]) {
        summary[event.userId] = {
          userId: event.userId,
          name: event.userName,
          events: [],
          totalSeconds: 0,
          clockInTime: null,
          firstClockIn: null,
          lastClockOut: null,
        };
      }
      summary[event.userId].events.push(event);
    });

    // Compute hours for each technician
    Object.values(summary).forEach(tech => {
      const sorted = [...tech.events].sort((a, b) => a.timestamp - b.timestamp);
      let clockInTime = null;

      sorted.forEach(event => {
        if (event.type === 'in') {
          clockInTime = event.timestamp;
          if (!tech.firstClockIn) tech.firstClockIn = event.timestamp;
        } else if (event.type === 'out' && clockInTime) {
          tech.totalSeconds += (event.timestamp - clockInTime) / 1000;
          tech.lastClockOut = event.timestamp;
          clockInTime = null;
        }
      });

      // Still clocked in
      if (clockInTime) {
        tech.totalSeconds += (Date.now() - clockInTime) / 1000;
        tech.stillClockedIn = true;
      }
    });

    return Object.values(summary).sort((a, b) => a.name?.localeCompare(b.name));
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleTimeString('en-KE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '0m';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    return `${Math.floor(seconds / 3600)}h ago`;
  };

  const getTotalClockedInTime = (userId) => {
    // Find this user's clock-in event from liveStatus
    const event = liveStatus.find(e => e.userId === userId);
    if (!event) return 0;
    return (Date.now() - event.timestamp) / 1000;
  };

  // CSV Export
  const exportCSV = async () => {
    setExporting(true);
    try {
      const summary = getAttendanceSummary();
      const dateLabel = selectedDate;

      let csv = `Attendance Report - ${dateLabel}\n`;
      csv += `Generated: ${new Date().toLocaleString('en-KE')}\n\n`;
      csv += `Name,First Clock In,Last Clock Out,Total Hours,Sessions,Status\n`;

      summary.forEach(tech => {
        const sessions = Math.floor(tech.events.filter(e => e.type === 'out').length);
        const status = tech.stillClockedIn ? 'Still Clocked In' : 'Clocked Out';
        csv += `"${tech.name}","${formatTime(tech.firstClockIn)}","${formatTime(tech.lastClockOut)}","${(tech.totalSeconds / 3600).toFixed(2)}","${sessions}","${status}"\n`;
      });

      csv += `\nDetailed Events\n`;
      csv += `Name,Type,Time,Location,Duration\n`;

      allEvents.forEach(event => {
        const lat = event.location?.latitude?.toFixed(6) || '';
        const lng = event.location?.longitude?.toFixed(6) || '';
        const loc = lat && lng ? `${lat}, ${lng}` : 'N/A';
        const duration = event.sessionDurationFormatted || '';
        csv += `"${event.userName}","${event.type === 'in' ? 'Clock In' : 'Clock Out'}","${formatTime(event.timestamp)}","${loc}","${duration}"\n`;
      });

      // Download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance_${dateLabel}.csv`;
      link.click();
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error('❌ Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const attendanceSummary = activeTab === 'history' ? getAttendanceSummary() : [];
  const totalClockedIn = liveStatus.length;
  const totalTechnicians = technicians.length;

  return (
    <div className="clock-monitor">
      {/* Header */}
      <div className="monitor-header">
        <button onClick={onBack} className="back-button">← Back</button>
        <div className="monitor-header-info">
          <h1>⏰ Clock Monitor</h1>
          <div className="live-badge">
            <span className="live-dot"></span>
            Live · {formatTimeAgo(lastUpdate)}
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="monitor-stats">
        <div className="monitor-stat clocked-in-stat">
          <div className="monitor-stat-value">{totalClockedIn}</div>
          <div className="monitor-stat-label">Clocked In Now</div>
        </div>
        <div className="monitor-stat">
          <div className="monitor-stat-value">{totalTechnicians - totalClockedIn}</div>
          <div className="monitor-stat-label">Not Clocked In</div>
        </div>
        <div className="monitor-stat">
          <div className="monitor-stat-value">{totalTechnicians}</div>
          <div className="monitor-stat-label">Total Team</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="monitor-tabs">
        <button
          className={`monitor-tab ${activeTab === 'live' ? 'active' : ''}`}
          onClick={() => setActiveTab('live')}
        >
          🟢 Live Status
        </button>
        <button
          className={`monitor-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('history');
            loadHistoricalEvents(selectedDate);
          }}
        >
          📅 Attendance History
        </button>
      </div>

      {/* LIVE STATUS TAB */}
      {activeTab === 'live' && (
        <div className="monitor-content">
          {/* Clocked In */}
          <div className="monitor-section">
            <div className="monitor-section-header">
              <h2>✅ Currently Clocked In ({totalClockedIn})</h2>
            </div>

            {liveStatus.length === 0 ? (
              <div className="empty-monitor">
                <div className="empty-icon">😴</div>
                <p>No technicians are currently clocked in</p>
              </div>
            ) : (
              <div className="live-cards">
                {liveStatus.map(event => (
                  <div key={event.userId} className="live-card clocked-in-card">
                    <div className="live-card-avatar">
                      {event.userName?.charAt(0).toUpperCase()}
                    </div>
                    <div className="live-card-info">
                      <div className="live-card-name">{event.userName}</div>
                      <div className="live-card-meta">
                        <span className="clocked-in-time">
                          ⏰ Clocked in at {formatTime(event.timestamp)}
                        </span>
                        <span className="session-elapsed">
                          · {formatDuration(getTotalClockedInTime(event.userId))} elapsed
                        </span>
                      </div>
                      {event.location && (
                        <div className="live-card-location">
                          <span className="location-text">
                            📍 {formatCoordinates(
                              event.location.latitude,
                              event.location.longitude,
                              event.location.accuracy
                            )}
                          </span>
                          <button
                            className="navigate-btn-small"
                            onClick={() => openInGoogleMaps(
                              event.location.latitude,
                              event.location.longitude,
                              `${event.userName} Clock In`
                            )}
                          >
                            🗺️ Map
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="live-status-pill">🟢 IN</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Not Clocked In */}
          <div className="monitor-section">
            <div className="monitor-section-header">
              <h2>⭕ Not Clocked In ({totalTechnicians - totalClockedIn})</h2>
            </div>

            <div className="absent-list">
              {technicians
                .filter(tech => !liveStatus.find(e => e.userId === tech.id))
                .map(tech => (
                  <div key={tech.id} className="absent-card">
                    <div className="absent-avatar">
                      {tech.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="absent-info">
                      <div className="absent-name">{tech.name}</div>
                      <div className="absent-phone">{tech.phone || 'No phone'}</div>
                    </div>
                    <div className="absent-pill">⭕ OUT</div>
                  </div>
                ))}

              {technicians.filter(tech => !liveStatus.find(e => e.userId === tech.id)).length === 0 && (
                <div className="empty-monitor">
                  <p>🎉 All technicians are clocked in!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* HISTORY TAB */}
      {activeTab === 'history' && (
        <div className="monitor-content">
          {/* Date Picker + Export */}
          <div className="history-controls">
            <div className="date-picker-wrapper">
              <label>📅 Select Date</label>
              <input
                type="date"
                value={selectedDate}
                max={getLocalDateString()}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="date-picker"
              />
            </div>
            <button
              className="export-btn"
              onClick={exportCSV}
              disabled={exporting || allEvents.length === 0}
            >
              {exporting ? '⏳ Exporting...' : '⬇️ Export CSV'}
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading attendance data...</p>
            </div>
          ) : allEvents.length === 0 ? (
            <div className="empty-monitor">
              <div className="empty-icon">📭</div>
              <p>No clock events found for {selectedDate}</p>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="attendance-summary">
                {attendanceSummary.map(tech => (
                  <div key={tech.userId} className="attendance-card">
                    <div className="attendance-card-header">
                      <div className="attendance-avatar">
                        {tech.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="attendance-name-block">
                        <div className="attendance-name">{tech.name}</div>
                        <div className="attendance-sessions">
                          {tech.events.filter(e => e.type === 'out').length} session(s)
                        </div>
                      </div>
                      <div className={`attendance-status-pill ${tech.stillClockedIn ? 'still-in' : 'clocked-out'}`}>
                        {tech.stillClockedIn ? '🟢 Still In' : '🔴 Clocked Out'}
                      </div>
                    </div>

                    <div className="attendance-times">
                      <div className="att-time-item">
                        <span className="att-label">First In</span>
                        <span className="att-value green">{formatTime(tech.firstClockIn)}</span>
                      </div>
                      <div className="att-time-item">
                        <span className="att-label">Last Out</span>
                        <span className="att-value red">{formatTime(tech.lastClockOut)}</span>
                      </div>
                      <div className="att-time-item">
                        <span className="att-label">Total Hours</span>
                        <span className="att-value highlight">{formatDuration(tech.totalSeconds)}</span>
                      </div>
                    </div>

                    {/* Event Timeline */}
                    <div className="event-timeline">
                      {tech.events
                        .sort((a, b) => a.timestamp - b.timestamp)
                        .map((event, idx) => (
                          <div key={event.id} className="timeline-event">
                            <div className={`timeline-dot ${event.type === 'in' ? 'dot-in' : 'dot-out'}`}></div>
                            <div className="timeline-info">
                              <span className={`timeline-type ${event.type === 'in' ? 'type-in' : 'type-out'}`}>
                                {event.type === 'in' ? '→ Clock In' : '← Clock Out'}
                              </span>
                              <span className="timeline-time">{formatTime(event.timestamp)}</span>
                              {event.sessionDurationFormatted && (
                                <span className="timeline-duration">({event.sessionDurationFormatted})</span>
                              )}
                              {event.location && (
                                <button
                                  className="map-link"
                                  onClick={() => openInGoogleMaps(
                                    event.location.latitude,
                                    event.location.longitude,
                                    `${event.userName} ${event.type === 'in' ? 'Clock In' : 'Clock Out'}`
                                  )}
                                >
                                  🗺️
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SupervisorClockMonitor;
